import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function Login({ onLogin }) {
  // 'email' | 'otp' | 'password'  ('password' = legacy escape hatch during transition)
  const [stage, setStage] = useState('email');

  const [email, setEmail] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [code, setCode] = useState('');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const codeRef = useRef(null);

  const reset = () => { setError(''); setInfo(''); };

  // ── Step 1: request email OTP ──────────────────────────────────────
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    reset();
    setLoading(true);
    try {
      const res = await axios.post('/api/auth/request-email-otp', { email });
      setEmailHint(res.data.email_hint || email);
      setStage('otp');
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשליחת הקוד');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: verify OTP ─────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    reset();
    setLoading(true);
    try {
      const res = await axios.post('/api/auth/verify-email-otp', { code });
      onLogin(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאת אימות');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    reset();
    try {
      await axios.post('/api/auth/resend-email-otp');
      setInfo('קוד חדש נשלח למייל');
      setCode('');
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשליחת קוד');
    }
  };

  // ── Legacy password login (transition escape hatch — remove in PR 5) ─
  const handlePassword = async (e) => {
    e.preventDefault();
    reset();
    setLoading(true);
    try {
      const res = await axios.post('/api/auth/login', { username, password });
      if (res.data?.otp_required) {
        setError('נדרש קוד דואר אלקטרוני — פנה למנהל');
      } else {
        onLogin(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'שם משתמש או סיסמה שגויים');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP entry screen ───────────────────────────────────────────────
  if (stage === 'otp') {
    return (
      <div className="login-page">
        <form className="login-card" onSubmit={handleVerifyOtp}>
          <img src="/logo.svg" alt="Huri-Connect" className="login-logo" />
          <p>
            שלחנו קוד לאימייל שלך
            {emailHint && <><br /><span style={{ fontWeight: 600 }}>{emailHint}</span></>}
          </p>
          {error && <div className="login-error">{error}</div>}
          {info && <div className="login-info">{info}</div>}
          <input
            ref={codeRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="קוד בן 6 ספרות"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.3rem' }}
            maxLength={6}
          />
          <button type="submit" disabled={loading || code.length < 6}>
            {loading ? 'מאמת...' : 'אימות'}
          </button>
          <button type="button" className="login-resend" onClick={handleResend}>
            לא קיבלת קוד? שלח שוב
          </button>
          <button
            type="button"
            className="login-resend"
            onClick={() => { setStage('email'); setCode(''); reset(); }}
            style={{ marginTop: '0.25rem' }}
          >
            חזרה לשינוי אימייל
          </button>
        </form>
      </div>
    );
  }

  // ── Legacy password screen ─────────────────────────────────────────
  if (stage === 'password') {
    return (
      <div className="login-page">
        <form className="login-card" onSubmit={handlePassword}>
          <img src="/logo.svg" alt="Huri-Connect" className="login-logo" />
          <p>כניסה עם שם משתמש וסיסמה</p>
          {error && <div className="login-error">{error}</div>}
          <input
            type="text"
            placeholder="שם משתמש"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoFocus
          />
          <input
            type="password"
            placeholder="סיסמה"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <button type="submit" disabled={loading}>
            {loading ? 'מתחבר...' : 'כניסה'}
          </button>
          <Link to="/forgot-password" className="login-forgot">שכחתי סיסמה</Link>
          <button
            type="button"
            className="login-resend"
            onClick={() => { setStage('email'); reset(); }}
            style={{ marginTop: '0.25rem' }}
          >
            חזרה לכניסה עם אימייל
          </button>
        </form>
      </div>
    );
  }

  // ── Email entry screen (default) ───────────────────────────────────
  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleRequestOtp}>
        <img src="/logo.svg" alt="Huri-Connect" className="login-logo" />
        <p>שליטה וניהול במבט אחד</p>
        {error && <div className="login-error">{error}</div>}
        <input
          type="email"
          inputMode="email"
          placeholder="כתובת אימייל"
          value={email}
          onChange={e => setEmail(e.target.value)}
          dir="ltr"
          style={{ textAlign: 'left' }}
          autoFocus
        />
        <button type="submit" disabled={loading || !email.trim()}>
          {loading ? 'שולח...' : 'שלח קוד'}
        </button>
        <button
          type="button"
          className="login-resend"
          onClick={() => { setStage('password'); reset(); }}
          style={{ marginTop: '0.75rem' }}
        >
          כניסה עם שם משתמש וסיסמה
        </button>
      </form>
    </div>
  );
}

export default Login;
